import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const workRoles = ['PR', 'Barman', 'Cameriere', 'Bagnino', 'Ragazza Immagine', 'Altro'];

export default function LavoraConNoi() {
  const [workFormData, setWorkFormData] = useState({ name: '', contact: '', role: '' });
  const [workSubmitting, setWorkSubmitting] = useState(false);

  const handleWorkSubmit = async (e) => {
    e.preventDefault();
    if (!workFormData.name || !workFormData.contact || !workFormData.role) {
      toast.error('Compila tutti i campi');
      return;
    }
    setWorkSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/work-with-us/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workFormData)
      });
      if (response.ok) {
        toast.success('Candidatura inviata! Ti contatteremo presto.');
        setWorkFormData({ name: '', contact: '', role: '' });
      } else {
        toast.error('Errore durante l\'invio. Riprova.');
      }
    } catch (error) {
      toast.error('Errore di connessione. Riprova.');
    }
    setWorkSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Lavora con Noi | La Villa - Castelfranco Emilia</title>
        <meta name="description" content="Entra nel team de La Villa: PR, Barman, Cameriere, Bagnino, Ragazza Immagine. Invia la tua candidatura." />
        <meta property="og:title" content="Lavora con Noi | La Villa" />
        <meta property="og:description" content="Entra nel team de La Villa. Lasciaci un tuo recapito, ti contatteremo entro 48 ore." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-night min-h-screen text-ivory" data-testid="lavora-con-noi-page">
        <section className="pt-36 pb-24 md:pt-44 md:pb-32 px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Entra nel Team
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight mb-4">
                Lavora con Noi
              </h1>
              <p className="font-body text-stone-400">
                PR, Barman, Cameriere, Bagnino, Ragazza Immagine?<br />
                Lasciaci un tuo recapito.
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleWorkSubmit}
              className="space-y-6 p-8 border border-champagne/30 bg-gradient-to-b from-stone-900/50 to-stone-900/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              data-testid="work-form"
            >
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-700">
                <Briefcase className="text-champagne" size={24} />
                <span className="font-heading text-xl text-ivory">Candidatura</span>
              </div>

              <div>
                <Label htmlFor="work-name" className="text-stone-300 text-sm">Nome e Cognome</Label>
                <Input
                  id="work-name"
                  type="text"
                  placeholder="Il tuo nome completo"
                  value={workFormData.name}
                  onChange={(e) => setWorkFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="work-name"
                />
              </div>

              <div>
                <Label htmlFor="work-contact" className="text-stone-300 text-sm">Email o WhatsApp</Label>
                <Input
                  id="work-contact"
                  type="text"
                  placeholder="email@esempio.com o +39..."
                  value={workFormData.contact}
                  onChange={(e) => setWorkFormData(prev => ({ ...prev, contact: e.target.value }))}
                  className="mt-2 bg-stone-800/50 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne"
                  data-testid="work-contact"
                />
              </div>

              <div>
                <Label htmlFor="work-role" className="text-stone-300 text-sm">Ruolo Desiderato</Label>
                <Select
                  value={workFormData.role}
                  onValueChange={(value) => setWorkFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger
                    className="mt-2 bg-stone-800/50 border-stone-700 text-ivory focus:border-champagne"
                    data-testid="work-role"
                  >
                    <SelectValue placeholder="Seleziona un ruolo" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {workRoles.map((role) => (
                      <SelectItem
                        key={role}
                        value={role}
                        className="text-ivory hover:bg-champagne/20 focus:bg-champagne/20"
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={workSubmitting}
                className="w-full bg-champagne text-night hover:bg-ivory py-6 text-sm uppercase tracking-widest mt-4"
                data-testid="work-submit"
              >
                {workSubmitting ? (
                  'Invio in corso...'
                ) : (
                  <>
                    <Briefcase size={16} className="mr-2" />
                    Invia Candidatura
                  </>
                )}
              </Button>

              <p className="text-center text-stone-500 text-xs mt-4">
                Ti contatteremo entro 48 ore.
              </p>
            </motion.form>
          </div>
        </section>
      </div>
    </>
  );
}
